import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser, UserRole } from '../contexts/UserContext';
import Monogram from './layout/Monogram';

// Role configuration
const ROLE_CONFIG: Record<UserRole, { label: string; description: string; dashboard: string }> = {
  student: {
    label: 'Family',
    description: 'Browse mentors and manage your sessions',
    dashboard: '/mentors',
  },
  parent: {
    label: 'Family',
    description: 'Browse mentors and manage your sessions',
    dashboard: '/mentors',
  },
  mentor: {
    label: 'Mentor',
    description: 'Requests, sessions, and your public profile',
    dashboard: '/mentor/dashboard',
  },
  counselor: {
    label: 'Counselor',
    description: 'Legacy role',
    dashboard: '/mentors',
  },
};

const RoleSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, roles: allRoles, setActiveRole } = useUser();
  // Counselor portal was removed in the marketplace pivot; hide legacy counselor roles
  const roles = allRoles.filter((role) => role !== 'counselor');

  const handleRoleSelect = (role: UserRole) => {
    setActiveRole(role);
    navigate(ROLE_CONFIG[role].dashboard);
  };

  // If user only has one role, redirect immediately
  React.useEffect(() => {
    if (roles.length === 1) {
      handleRoleSelect(roles[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles]);

  // If no user, redirect to login
  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user || roles.length <= 1) {
    return null;
  }

  const firstName = user.name?.split(' ')[0] || 'there';

  return (
    <div
      className="section"
      style={{
        background: 'var(--warm)',
        minHeight: 'calc(100vh - 200px)',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div className="wrap" style={{ width: '100%' }}>
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <span className="eyebrow" style={{ display: 'block', marginBottom: 10 }}>
              Signed in · {user.email}
            </span>
            <h1 style={{ fontSize: 26 }}>Welcome back, {firstName}</h1>
            <p className="meta" style={{ marginTop: 8 }}>
              Your account holds {roles.length === 2 ? 'two roles' : `${roles.length} roles`}.
              Choose where to go — you can switch anytime from the top bar.
            </p>
          </div>

          {roles.map((role) => {
            const config = ROLE_CONFIG[role];
            return (
              <button
                key={role}
                className="card"
                onClick={() => handleRoleSelect(role)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  marginBottom: 14,
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer',
                  font: 'inherit',
                }}
              >
                <Monogram name={user.name} />
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: 17 }}>{config.label}</h3>
                  <p className="meta">{config.description}</p>
                </div>
                <span style={{ color: 'var(--cardinal)', fontSize: 18 }}>→</span>
              </button>
            );
          })}

          <p className="meta" style={{ textAlign: 'center', marginTop: 20 }}>
            Single-role accounts skip this step and go straight to their dashboard.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RoleSelectionPage;
